import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Search, Filter, X, ChevronLeft, ChevronRight, Calendar as CalIcon, ChevronDown, ChevronUp } from "lucide-react";
import api from "@/lib/api";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";

const HERO_IMG =
  "https://images.unsplash.com/photo-1522158637959-30385a09e0da?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHx3b3JzaGlwJTIwY29uY2VydCUyMHlvdXRoJTIwc2lsaG91ZXR0ZXxlbnwwfHx8fDE3ODIwMDU5MTF8MA&ixlib=rb-4.1.0&q=85";

const monthLabel = (yyyyMm) => {
  if (!yyyyMm) return "";
  const [y, m] = yyyyMm.split("-");
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleString("en", { month: "long", year: "numeric" });
};

export default function Gallery() {
  const [images, setImages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filters, setFilters] = useState({ category: "All", month: "", q: "" });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const params = {};
    if (filters.category && filters.category !== "All") params.category = filters.category;
    if (filters.month) params.month = filters.month;
    if (filters.q) params.q = filters.q;
    try {
      const r = await api.get("/gallery", { params });
      setImages(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/gallery/categories").then((r) => setCategories(["All", ...r.data]));
  }, []);
  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [filters.category, filters.month]);

  // Group by month for an editorial section feel
  const grouped = useMemo(() => {
    const map = new Map();
    images.forEach((img) => {
      const key = (img.event_date || img.created_at || "").slice(0, 7);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(img);
    });
    return Array.from(map.entries()).sort((a, b) => b[0].localeCompare(a[0]));
  }, [images]);

  const allFlat = images;
  const open = (img) => setLightboxIndex(allFlat.findIndex((i) => i.id === img.id));
  const close = () => setLightboxIndex(null);
  const next = () => setLightboxIndex((i) => (i + 1) % allFlat.length);
  const prev = () => setLightboxIndex((i) => (i - 1 + allFlat.length) % allFlat.length);
  const active = lightboxIndex != null ? allFlat[lightboxIndex] : null;

  return (
    <div data-testid="gallery-page" className="bg-[#050505] text-white">
      <section className="relative py-32 md:py-40 px-6 md:px-12 border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#050505]/60 to-[#050505]" />
        </div>
        <div className="relative max-w-7xl mx-auto">
          <p className="font-mono-accent text-xs tracking-[0.3em] uppercase text-[#ff5c00] mb-4">Gallery</p>
          <h1 className="font-display text-5xl md:text-7xl lg:text-8xl uppercase font-bold tracking-tighter leading-[0.9]">
            Memories <span className="text-[#ff5c00]">made</span>.
          </h1>
          <p className="text-zinc-300 text-lg md:text-xl max-w-3xl mt-8">
            Highlights from Sunday services, conventions, anniversaries and
            community moments — relive what God has been doing in our midst.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="sticky top-16 md:top-20 z-30 px-6 md:px-12 border-b border-white/10 bg-[#050505]/90 backdrop-blur-md">
        <div className="max-w-7xl mx-auto py-3 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => setFiltersOpen((o) => !o)}
            data-testid="gallery-filters-toggle"
            aria-expanded={filtersOpen}
            className="inline-flex items-center gap-2 font-mono-accent text-[11px] tracking-[0.2em] uppercase text-zinc-300 hover:text-[#ff5c00] transition-colors"
          >
            <Filter className="w-4 h-4 text-[#ff5c00]" />
            <span>Filters</span>
            {(filters.category !== "All" || filters.month || filters.q) && (
              <span className="ml-1 inline-flex items-center justify-center bg-[#ff5c00] text-white text-[10px] rounded-full w-5 h-5 leading-none">
                {[filters.category !== "All", !!filters.month, !!filters.q].filter(Boolean).length}
              </span>
            )}
            {filtersOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
          {(filters.category !== "All" || filters.month || filters.q) && (
            <button
              onClick={() => setFilters({ category: "All", month: "", q: "" })}
              data-testid="gallery-filters-clear"
              className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-zinc-500 hover:text-[#ff5c00]"
            >
              Clear All
            </button>
          )}
        </div>

        {filtersOpen && (
          <div data-testid="gallery-filters-panel" className="max-w-7xl mx-auto pb-4 flex flex-wrap items-center gap-3">
            <div className="flex flex-wrap gap-2">
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => setFilters({ ...filters, category: c })}
                  data-testid={`gallery-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`font-mono-accent text-[11px] tracking-[0.18em] uppercase px-3 py-1.5 rounded-sm border transition-colors ${
                    filters.category === c
                      ? "border-[#ff5c00] text-white bg-[#ff5c00]/10"
                      : "border-white/10 text-zinc-400 hover:text-white"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>

            <div className="ml-auto flex items-center gap-3 flex-wrap">
              <label className="flex items-center gap-2 text-xs text-zinc-400">
                <CalIcon className="w-3.5 h-3.5 text-[#ff5c00]" />
                <input
                  type="month"
                  value={filters.month}
                  onChange={(e) => setFilters({ ...filters, month: e.target.value })}
                  data-testid="gallery-filter-month"
                  className="bg-[#0a0a0d] border border-white/10 px-2 py-1.5 text-xs text-white focus:outline-none focus:border-[#ff5c00] rounded-sm"
                />
                {filters.month && (
                  <button onClick={() => setFilters({ ...filters, month: "" })} className="text-zinc-500 hover:text-[#ff5c00]" data-testid="gallery-clear-month">
                    <X className="w-3 h-3" />
                  </button>
                )}
              </label>

              <form
                onSubmit={(e) => { e.preventDefault(); load(); }}
                className="flex items-center gap-2"
              >
                <div className="relative">
                  <Search className="w-3.5 h-3.5 text-zinc-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="Search…"
                    value={filters.q}
                    onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                    data-testid="gallery-search"
                    className="bg-[#0a0a0d] border border-white/10 pl-8 pr-3 py-1.5 text-xs text-white focus:outline-none focus:border-[#ff5c00] rounded-sm w-44"
                  />
                </div>
                <button
                  type="submit"
                  data-testid="gallery-search-submit"
                  className="font-mono-accent text-[11px] uppercase tracking-widest px-3 py-1.5 rounded-sm border border-white/10 hover:border-[#ff5c00] hover:text-[#ff5c00] text-zinc-300"
                >
                  Go
                </button>
              </form>
            </div>
          </div>
        )}
      </section>

      {/* Grid */}
      <section className="py-12 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <p className="text-zinc-500 font-mono-accent text-xs tracking-widest uppercase">Loading…</p>
          ) : images.length === 0 ? (
            <p data-testid="gallery-empty" className="text-zinc-500 text-sm py-16 text-center">
              No images for this filter yet. Try a different category or month.
            </p>
          ) : (
            grouped.map(([month, items]) => (
              <div key={month} className="mb-14">
                <div className="flex items-end justify-between mb-6 border-b border-white/5 pb-3">
                  <h2 className="font-display text-2xl md:text-3xl uppercase tracking-tight">
                    {monthLabel(month) || "Recent"}
                  </h2>
                  <p className="font-mono-accent text-[10px] tracking-widest uppercase text-zinc-500">
                    {items.length} image{items.length === 1 ? "" : "s"}
                  </p>
                </div>

                <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4 [column-fill:_balance]">
                  {items.map((img, i) => (
                    <motion.button
                      key={img.id}
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.02 }}
                      onClick={() => open(img)}
                      data-testid={`gallery-img-${i}`}
                      className="group mb-4 break-inside-avoid block w-full text-left bg-[#0f0f14] border border-white/10 hover:border-[#ff5c00]/60 rounded-sm overflow-hidden transition-all"
                    >
                      <div className="relative">
                        <img
                          src={img.image_url}
                          alt={img.title || ""}
                          loading="lazy"
                          className="w-full h-auto block object-cover group-hover:opacity-90 transition-opacity"
                        />
                        <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/85 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                          {img.title && <p className="font-display text-sm uppercase tracking-tight line-clamp-1">{img.title}</p>}
                          <p className="font-mono-accent text-[10px] tracking-widest uppercase text-[#ff5c00]">
                            {img.category}{img.event_date ? ` · ${img.event_date}` : ""}
                          </p>
                        </div>
                      </div>
                    </motion.button>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {/* Lightbox */}
      <Dialog open={!!active} onOpenChange={(o) => !o && close()}>
        <DialogContent data-testid="gallery-lightbox" className="bg-[#050505] border border-white/10 text-white max-w-5xl p-2 sm:p-4">
          {active && (
            <div className="space-y-3">
              <div className="relative bg-black flex items-center justify-center">
                <img src={active.image_url} alt={active.title || ""} className="max-h-[75vh] w-auto mx-auto object-contain" />
                {allFlat.length > 1 && (
                  <>
                    <button
                      onClick={prev}
                      data-testid="gallery-lightbox-prev"
                      className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 hover:bg-[#ff5c00] text-white flex items-center justify-center"
                      aria-label="Previous"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      onClick={next}
                      data-testid="gallery-lightbox-next"
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 hover:bg-[#ff5c00] text-white flex items-center justify-center"
                      aria-label="Next"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </>
                )}
              </div>
              <div className="flex flex-wrap items-center justify-between gap-3 px-2 pb-1">
                <div>
                  {active.title && <p className="font-display text-xl uppercase tracking-tight">{active.title}</p>}
                  {active.description && <p className="text-sm text-zinc-400 mt-1 max-w-2xl">{active.description}</p>}
                </div>
                <span className="font-mono-accent text-[10px] tracking-widest uppercase text-[#ff5c00]">
                  {active.category}{active.event_date ? ` · ${active.event_date}` : ""}
                </span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
